// CorregirCoordenadasDialog.js
// "Corregir coordenadas" desde Centro de Control y Coordinación (requerimiento 2026-10-14, R12): solo mueve
// los puntos A (origen) y B (destino) del traslado. Disponible en cualquier estado; el backend lo rechaza si
// el control de servicio está recibido o facturado y deja el cambio en el historial del traslado.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Col, InputNumber, Modal, Radio, Row, Space, Spin, Typography, message } from "antd";
import { AimOutlined, UndoOutlined } from "@ant-design/icons";
import { MapCanvas as GoogleMap, MapMarker as Marker, useMapLoader, fitMapBounds } from "../../../geo/Map";
import api from "../../../axiosConfig";
import { geoJsonToLatLng } from "../../../utils/coordinates";

const { Text } = Typography;

// Mismas opciones que los demás mapas (un solo cargador de Google Maps en la app)
const BOGOTA = { lat: 4.711, lng: -74.0721 };
const PUNTOS = {
  origen: { letra: "A", titulo: "Origen (A)", color: "#52c41a" },
  destino: { letra: "B", titulo: "Destino (B)", color: "#f5222d" },
};

const redondear = (valor) => Math.round(valor * 1e7) / 1e7;
const igual = (a, b) => (!a && !b) || (a && b && Math.abs(a.lat - b.lat) < 1e-7 && Math.abs(a.lng - b.lng) < 1e-7);

function CorregirCoordenadasDialog({ open, onClose, traslado, onCorregido }) {
  const iniciales = useMemo(
    () => ({
      origen: geoJsonToLatLng(traslado?.origen_coordenadas),
      destino: geoJsonToLatLng(traslado?.destino_coordenadas),
    }),
    [traslado]
  );
  const [puntos, setPuntos] = useState(iniciales);
  const [activo, setActivo] = useState("origen");
  const [mapa, setMapa] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const { isLoaded, loadError } = useMapLoader();

  useEffect(() => {
    if (open) {
      setPuntos(iniciales);
      setActivo(iniciales.origen || !iniciales.destino ? "origen" : "destino");
    }
  }, [open, iniciales]);

  // Encuadrar ambos puntos al abrir
  useEffect(() => {
    if (!mapa || !open || !isLoaded) return;
    const conocidos = [iniciales.origen, iniciales.destino].filter(Boolean);
    if (conocidos.length === 2) {
      fitMapBounds(mapa, conocidos, 60);
    } else {
      mapa.setCenter(conocidos[0] || BOGOTA);
      mapa.setZoom(conocidos.length ? 15 : 11);
    }
  }, [mapa, open, iniciales, isLoaded]);

  const mover = useCallback((lado, latLng) => {
    setPuntos((prev) => ({ ...prev, [lado]: { lat: redondear(latLng.lat()), lng: redondear(latLng.lng()) } }));
  }, []);

  const cambiarCampo = (lado, eje, valor) => {
    if (valor === null || valor === undefined || Number.isNaN(Number(valor))) return;
    setPuntos((prev) => ({ ...prev, [lado]: { ...(prev[lado] || iniciales[lado] || BOGOTA), [eje]: Number(valor) } }));
  };

  const centrarEn = (lado) => {
    if (mapa && puntos[lado]) {
      mapa.panTo(puntos[lado]);
      mapa.setZoom(17);
    }
  };

  const cambios = useMemo(
    () => Object.keys(PUNTOS).filter((lado) => puntos[lado] && !igual(puntos[lado], iniciales[lado])),
    [puntos, iniciales]
  );

  const guardar = async () => {
    if (!cambios.length) {
      message.info("No hay cambios en las coordenadas");
      return;
    }
    const datos = {};
    cambios.forEach((lado) => {
      datos[lado] = puntos[lado];
    });
    setGuardando(true);
    try {
      const { data } = await api.put(`/traslados/${traslado.id}/corregir-coordenadas`, datos);
      message.success(data?.message || "Coordenadas corregidas");
      onCorregido?.({
        origen_coordenadas: data?.data?.origen_coordenadas ?? traslado.origen_coordenadas,
        destino_coordenadas: data?.data?.destino_coordenadas ?? traslado.destino_coordenadas,
      });
      onClose();
    } catch (error) {
      message.error(error.response?.data?.message || "No se pudieron corregir las coordenadas");
    } finally {
      setGuardando(false);
    }
  };

  const campos = (lado) => {
    const { titulo, color } = PUNTOS[lado];
    const punto = puntos[lado];
    const direccion = traslado?.[`${lado}_direccion`] || traslado?.[`${lado}_lugar`];
    return (
      <Col xs={24} md={12} key={lado}>
        <div
          style={{
            border: `1px solid ${activo === lado ? color : "#f0f0f0"}`,
            borderRadius: 8,
            padding: 10,
            height: "100%",
          }}
        >
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text strong style={{ color }}>
              {titulo}
            </Text>
            <Space size={4}>
              {cambios.includes(lado) && <Text type="warning">Modificado</Text>}
              <Button size="small" icon={<AimOutlined />} disabled={!punto} onClick={() => centrarEn(lado)} />
              <Button
                size="small"
                icon={<UndoOutlined />}
                disabled={!cambios.includes(lado)}
                onClick={() => setPuntos((prev) => ({ ...prev, [lado]: iniciales[lado] }))}
                title="Restablecer"
              />
            </Space>
          </Space>
          {direccion && (
            <Text type="secondary" style={{ display: "block", fontSize: 12, margin: "4px 0" }} ellipsis={{ tooltip: direccion }}>
              {direccion}
            </Text>
          )}
          <Space.Compact style={{ width: "100%" }}>
            <InputNumber
              style={{ width: "50%" }}
              addonBefore="Lat"
              value={punto?.lat}
              step={0.0001}
              precision={7}
              onChange={(v) => cambiarCampo(lado, "lat", v)}
              onFocus={() => setActivo(lado)}
            />
            <InputNumber
              style={{ width: "50%" }}
              addonBefore="Lng"
              value={punto?.lng}
              step={0.0001}
              precision={7}
              onChange={(v) => cambiarCampo(lado, "lng", v)}
              onFocus={() => setActivo(lado)}
            />
          </Space.Compact>
        </div>
      </Col>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Corregir coordenadas - Traslado #${traslado?.id ?? ""}`}
      width={900}
      destroyOnClose
      footer={[
        <Button key="cancelar" onClick={onClose}>
          Cancelar
        </Button>,
        <Button key="guardar" type="primary" loading={guardando} disabled={!cambios.length} onClick={guardar}>
          Guardar corrección
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Arrastre los puntos A y B o haga clic en el mapa para ubicar el punto seleccionado"
        description="Solo se corrigen las coordenadas de este traslado (no cambia el domicilio del paciente ni la institución). El cambio queda en el historial del traslado."
      />
      <Space style={{ marginBottom: 8 }}>
        <Text>Ubicar con clic:</Text>
        <Radio.Group value={activo} onChange={(e) => setActivo(e.target.value)} buttonStyle="solid" size="small">
          <Radio.Button value="origen">Origen (A)</Radio.Button>
          <Radio.Button value="destino">Destino (B)</Radio.Button>
        </Radio.Group>
      </Space>
      <div style={{ height: 420, borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
        {loadError ? (
          <Alert type="error" showIcon message="No se pudo cargar Google Maps. Puede ingresar las coordenadas manualmente." />
        ) : !isLoaded ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Spin tip="Cargando mapa..." />
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={iniciales.origen || iniciales.destino || BOGOTA}
            zoom={13}
            onLoad={setMapa}
            onUnmount={() => setMapa(null)}
            onClick={(e) => e.latLng && mover(activo, e.latLng)}
            options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: true, clickableIcons: false }}
          >
            {Object.entries(PUNTOS).map(([lado, { letra, titulo }]) =>
              puntos[lado] ? (
                <Marker
                  key={lado}
                  position={puntos[lado]}
                  label={{ text: letra, color: "#ffffff", fontWeight: "bold" }}
                  title={titulo}
                  draggable
                  onDragStart={() => setActivo(lado)}
                  onDragEnd={(e) => e.latLng && mover(lado, e.latLng)}
                  onClick={() => setActivo(lado)}
                  opacity={activo === lado ? 1 : 0.75}
                />
              ) : null
            )}
          </GoogleMap>
        )}
      </div>
      <Row gutter={[12, 12]}>{Object.keys(PUNTOS).map(campos)}</Row>
    </Modal>
  );
}

CorregirCoordenadasDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  traslado: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    origen_coordenadas: PropTypes.object,
    destino_coordenadas: PropTypes.object,
  }).isRequired,
  onCorregido: PropTypes.func,
};

export default CorregirCoordenadasDialog;
